import { PageBoundary } from "../../../packages/ui/Boundary";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  Users,
  ClipboardCheck,
  Library,
  Folder,
  Code2,
  Boxes,
  Bell,
  Settings,
  ChevronsUpDown,
  Layers,
  Search,
  RefreshCw,
  ChevronRight,
  GraduationCap,
  SquarePen,
  BarChart3,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
} from "lucide-react";
import {
  DEMO,
  useApi,
  useSession,
  type Account,
} from "../../../packages/api/client";
import {
  Button,
  ErrorNotice,
  IconButton,
  Loading,
  SearchBox,
} from "../../../packages/ui";
import { Login } from "./Login";
import { FlowAutoLogin, FlowLoginCallback } from "./FlowLogin";
import { DirtyContext } from "./guard";
import style from "./shell.module.css";
import { ManagedCompany, ManagedAccount, ProfileAvatar } from "./ManagedIdentity";

const ManagedClasses = lazy(() => import("../teaching/ManagedTeaching").then(m=>({default:m.ManagedClasses})));
const ManagedSchedule = lazy(() => import("../teaching/ManagedTeaching").then(m=>({default:m.ManagedSchedule})));
const ManagedSchool = lazy(() => import("../operations/ManagedSchool"));
const Classes = lazy(() => import("../teaching/Classes"));
const FlowClasses = lazy(() => import("../teaching/FlowClasses"));
const Courses = lazy(() => import("../teaching/Courses"));
const StudioCourses = lazy(() => import("../teaching/StudioCourses"));
const StudioResources = lazy(() =>
  import("../teaching/StudioMaterials").then((m) => ({
    default: m.StudioResourceLibrary,
  })),
);
const StudioPlans = lazy(() => import("../teaching/StudioPlans"));
const Schedule = lazy(() => import("../teaching/Schedule"));
const Assignments = lazy(() => import("../evaluation/Assignments"));
const Assessments = lazy(() => import("../evaluation/Assessments"));
const Resources = lazy(() => import("../resources/Resources"));
const Equipment = lazy(() => import("../resources/Equipment"));
const Coding = lazy(() => import("../creation/Coding"));
const Scratch = lazy(() => import("../creation/Scratch"));
const Reports = lazy(() => import("../operations/Reports"));
const Notifications = lazy(() => import("../operations/Notifications"));
const SettingsPage = lazy(() => import("../operations/Settings"));
const Official = lazy(() => import("../resources/Official"));
const Tools = lazy(() => import("../creation/Tools"));

const groups = [
  {
    label: "教学",
    items: [
      ["classes", "班级", Users],
      ["schedule", "课表", CalendarDays],
      ["courses", "课程", BookOpen],
      ["assignments", "作业", ClipboardCheck],
      ["assessments", "测评", SquarePen],
      ["reports", "学情", BarChart3],
    ],
  },
  {
    label: "资源与工具",
    items: [
      ["files", "资料库", Folder],
      ["scratch", "Scratch", Sparkles],
      ["coding", "编程题库", Code2],
      ["creation", "应用", Boxes],
      ["officialContent", "官方内容", Library],
      ["equipment", "器材", Boxes],
    ],
  },
] as const;
export function App() {
  const { token, account, setAccount, logout } = useSession();
  const accountQuery = useApi<Account>("/teacher/account");
  const navigate = useNavigate();
  const location = useLocation();
  const view = location.pathname.split("/")[1] || "classes";
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const setDirty = useCallback(
    (id: string, dirty: boolean) =>
      setDirtyIds((old) => {
        const next = new Set(old);
        if (dirty) next.add(id);
        else next.delete(id);
        return next;
      }),
    [],
  );
  const dirty = dirtyIds.size > 0;
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("有未保存的修改，放弃并离开？")) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);
  useEffect(() => {
    const fn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  }, [dirty]);
  useEffect(() => {
    if (accountQuery.data) {
      setAccount(accountQuery.data);
      if (accountQuery.data.id.startsWith("flow-"))
        document.title = "教学流";
    }
  }, [accountQuery.data, setAccount]);
  useEffect(() => {
    if (view === "today" || !location.pathname || location.pathname === "/")
      navigate("/classes", {
        replace: true,
      });
    if (view === "courseDetail")
      navigate(`/courses?${new URLSearchParams(location.search)}`, {
        replace: true,
      });
  }, [view, location.pathname, location.search, navigate, account?.id]);
  if (view === "flow-auth") return <FlowLoginCallback />;
  if (view === "flow-login") return <FlowAutoLogin />;
  if (!token) return <Login />;
  if (accountQuery.isPending) return <Loading />;
  if (accountQuery.error)
    return (
      <div className="auth-error">
        <ErrorNotice
          error={accountQuery.error}
          retry={() => accountQuery.refetch()}
        />
        <Button onClick={logout}>退出账号</Button>
      </div>
    );
  if (!account) return <Loading />;
  if (["super_admin", "operator"].includes(account.role))
    return (
      <div className="auth-error">
        <Button
          variant="primary"
          onClick={() =>
            locationAssign(import.meta.env.VITE_PLATFORM_ENTRY || "/")
          }
        >
          进入平台管理
        </Button>
        <Button onClick={logout}>退出账号</Button>
      </div>
    );
  const managed = account.id.startsWith("flow-");
  const navGroups = groups;
  const title =
    [
      ...navGroups.flatMap((g) => g.items.map((i) => [i[0], i[1]])),
      ["students", "学生"],
      ["flow", "教学配置"],
      ["schoolManagement", "学校管理"],
      ["notifications", "通知"],
      ["announcements", "公告"],
      ["account", "账号"],
      ["officialQuestions", "官方题集"],
    ].find((i) => i[0] === view)?.[1] || "页面未找到";
  const pages: Record<string, React.ReactNode> = {
    flow: managed ? <StudioPlans /> : <FlowClasses />,
    classes: managed ? <ManagedClasses /> : <Classes />,
    students: managed ? <ManagedClasses studentsOnly /> : <Classes studentsOnly />,
    schedule: managed ? <ManagedSchedule /> : <Schedule />,
    courses: managed ? <StudioCourses /> : <Courses />,
    assignments: <Assignments />,
    assessments: <Assessments />,
    files: managed ? <StudioResources /> : <Resources />,
    equipment: <Equipment />,
    coding: <Coding />,
    scratch: <Scratch />,
    reports: <Reports />,
    notifications: <Notifications />,
    notificationInbox: <Notifications />,
    announcements: <Notifications announcements />,
    announcementCompose: <Notifications key="compose" announcements compose />,
    account: managed ? (
      <ManagedAccount account={account} />
    ) : (
      <SettingsPage />
    ),
    schoolManagement: managed ? <ManagedSchool /> : <SettingsPage school />,
    officialContent: <Official />,
    officialQuestions: <Official key="questions" questionsOnly />,
    creation: <Tools />,
  };
  return (
    <DirtyContext.Provider value={setDirty}>
      <div className={`${style.shell} ${managed ? style.managedShell : ""} ${collapsed ? style.collapsed : ""}`}>
        <aside className={style.sidebar}>
          {managed ? <ManagedCompany account={account} collapsed={collapsed} dirty={dirty} /> : <button
            className={style.brand}
            onClick={() => navigate(managed ? "/courses" : "/classes")}
          >
            <span className={style.logo}>
              <Layers size={21} />
            </span>
            {!collapsed && (
              <>
                <strong>
                  {managed ? "教学流" : account.schoolName || "汇聚教育"}
                </strong>
                <ChevronsUpDown size={13} />
              </>
            )}
          </button>}
          {!collapsed && (
            <div className={style.navSearch}>
              <SearchBox
                value={search}
                onChange={setSearch}
                placeholder="查找功能"
              />
            </div>
          )}
          <nav className={style.nav} aria-label="教师主导航">
            {false && (
              <section>
                <button
                  title="Flow 班级"
                  className={view === "flow" ? style.selected : ""}
                  onClick={() => navigate("/flow")}
                >
                  <Users size={18} />
                  {!collapsed && <span>Flow 班级</span>}
                </button>
              </section>
            )}
            {navGroups.map((group) => (
              <section key={group.label}>
                {!collapsed && (
                  <span className={style.groupLabel}>{group.label}</span>
                )}
                {group.items
                  .filter((i) => i[1].includes(search))
                  .map(([id, text, Icon]) => (
                    <button
                      title={text}
                      aria-current={view === id ? "page" : undefined}
                      className={
                        view === id || (id === "classes" && view === "students")
                          ? style.selected
                          : ""
                      }
                      key={id}
                      onClick={() => navigate(`/${id}`)}
                    >
                      <Icon size={18} />
                      {!collapsed && <span>{text}</span>}
                    </button>
                  ))}
              </section>
            ))}
            {account.role === "admin" && (
              <section>
                <button
                  title="学校管理"
                  aria-current={
                    view === "schoolManagement" ? "page" : undefined
                  }
                  className={view === "schoolManagement" ? style.selected : ""}
                  onClick={() => navigate("/schoolManagement")}
                >
                  <GraduationCap size={18} />
                  {!collapsed && <span>学校管理</span>}
                </button>
              </section>
            )}
          </nav>
          <div className={style.sidebarFooter}>
            {(
              <button
                title="通知"
                className={view === "notifications" ? style.selected : ""}
                onClick={() => navigate("/notifications")}
              >
                <Bell size={18} />
                {!collapsed && <span>通知</span>}
              </button>
            )}
            <button title="个人账号" onClick={() => navigate("/account")}>
              <ProfileAvatar key={account.avatar} account={account} />
              {!collapsed && (
                <>
                  <span>{account.name}</span>
                  <Settings size={16} />
                </>
              )}
            </button>
          </div>
        </aside>
        <section className={style.workspace}>
          <header className={style.header}>
            <IconButton
              label={collapsed ? "展开侧栏" : "收起侧栏"}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <PanelLeftOpen size={17} />
              ) : (
                <PanelLeftClose size={17} />
              )}
            </IconButton>
            <span className={style.headerDivider} />
            <span className={style.breadcrumb}>
              {managed ? "教学流" : "教学空间"}
              <ChevronRight size={13} />
              <strong>{title}</strong>
            </span>
            <span className="spacer" />
            {DEMO && <span className="demo-indicator">演示数据</span>}
            <IconButton
              label="退出登录"
              onClick={() => {
                if (!dirty || window.confirm("有未保存的修改，仍要退出？"))
                  logout();
              }}
            >
              <LogOut size={16} />
            </IconButton>
          </header>
          <main className={style.content} key={account.id + view}>
            <PageBoundary key={view}>
              <Suspense fallback={<Loading />}>
                {pages[view] || <ErrorNotice error="此链接不存在，请从侧栏选择页面" />}
              </Suspense>
            </PageBoundary>
          </main>
        </section>
      </div>
    </DirtyContext.Provider>
  );
}
function locationAssign(url: string) {
  window.location.assign(url);
}
